import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PermissionsContainer from '../../components/PermissionsContainer';
import { useThemeEngine } from '../../theme/ThemeEngineContext';
import { useThemeTokens } from '../../theme/useThemeTokens';
import { checkContrast } from '../../theme/contrast';
import { compileTheme } from '../../theme/compile';
import { MAX_FONT_PX, MIN_FONT_PX, MAX_SPACE_UNIT_REM, MIN_SPACE_UNIT_REM } from '../../theme/resolve';
import { useCustomizeDraft } from './customize/useCustomizeDraft';
import { COLOR_GROUPS } from './customize/colorGroups';
import { ColorField, Section, SliderField, CHROME_FONT_SIZE } from './customize/controls';
import Gallery, { GALLERY_SECTIONS } from './customize/Gallery';
import ScenePreview from './customize/ScenePreview';
import { applyPreset } from './customize/presets';
import { useCustomizePresets, type PresetEntry } from './customize/useCustomizePresets';
import type { ThemeDoc } from '../../theme/types';
import themeService from '../../services/theme.service';

/**
 * Customize - the theme editor.
 *
 * Two scopes in one page. "My theme" is available to anyone who can reach the
 * route; "Organization default" changes what everybody sees and so its controls
 * sit behind the edit-org-theme component gate, mirrored server-side.
 *
 * Editing is live: every change goes into the theme engine, so the whole session
 * repaints and the draft can be judged against real screens by navigating away
 * and back. Nothing persists until Save or Publish.
 */


type Tab = 'colors' | 'type' | 'layout' | 'components';

const Customize: React.FC = () => {
  const engine = useThemeEngine();
  const draft = useCustomizeDraft();
  const { triplet } = useThemeTokens();
  const [tab, setTab] = useState<Tab>('colors');
  useFavoriteTab(tab, setTab, ['colors', 'type', 'layout', 'components']);
  const [gallerySection, setGallerySection] = useState<string | null>(null);
  /** Token the inspector sent us to, flashed so it is findable in a long panel. */
  const [highlighted, setHighlighted] = useState<string | null>(null);
  /** The token gallery is still here for reference; page samples lead. */
  const [showGallery, setShowGallery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * The user's pre-v2 hue/saturation tint, offered once for import.
   *
   * Without this their old customisation just silently stops applying the day
   * the new system takes over - the app would look "wrong" with no explanation
   * and no way back to what they had.
   */
  const [legacy, setLegacy] = useState<ThemeDoc | null>(null);

  useEffect(() => {
    let cancelled = false;
    themeService.getImportableLegacyTheme().then((doc) => {
      if (!cancelled) setLegacy(doc);
    });
    return () => { cancelled = true; };
  }, []);

  const answerLegacy = async (accept: boolean) => {
    if (accept && legacy) draft.patch(applyPreset(working, legacy));
    setLegacy(null);
    // Record the answer either way, so the offer is made once and not once per
    // visit. A failure here is not worth interrupting them over.
    try {
      await themeService.dismissLegacyImport();
    } catch { /* it will simply be offered again */ }
  };

  const { scope, mode, working, isDirty } = draft;

  /** The value a field shows: the draft's own, else whatever is inherited. */
  const effective = useCallback((group: 'tokens' | 'components', token: string): string => {
    const own = working[group]?.[mode]?.[token];
    if (own) return own;
    return triplet(token);
  }, [working, mode, triplet]);

  const isOverridden = useCallback(
    (group: 'tokens' | 'components', token: string) => Boolean(working[group]?.[mode]?.[token]),
    [working, mode],
  );

  const contrast = useMemo(
    () => checkContrast((token) => effective('tokens', token)),
    [effective],
  );
  const failing = contrast.filter((c) => c.failing);

  const onSave = async () => {
    setBusy(true);
    setError(null);
    try {
      await draft.save();
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail ?? 'Could not save. Your changes are still applied in this session.');
    } finally {
      setBusy(false);
    }
  };

  const publishing = scope === 'org';

  // The density lever is global by design - a draft retunes the whole app so it
  // can be judged on real screens. But the editor's OWN controls must not move
  // while you drag the thing that moves them: the panel reflows, the slider
  // slides out from under the cursor, and rows jump as you scan them.
  //
  // Pinning --space-unit here holds the editor at the stock rhythm; the preview
  // below re-applies the theme's value so it still shows the truth. The rest of
  // the app - sidebar, top bar, whatever page you navigate to next - is
  // untouched by this and updates live.
  const previewSpaceUnit = `${draft.working.layout?.spaceUnitRem ?? 0.25}rem`;

  // Keep the engine in step with the toggle. Deliberately NO cleanup that
  // resets it: an earlier version restored applyDraftGlobally(true) on unmount,
  // which meant turning site-wide preview OFF and then navigating away made the
  // confined draft suddenly paint every page - the exact thing the user had
  // just opted out of. A confined draft stays confined until they come back;
  // the engine simply keeps showing the saved theme elsewhere.
  useEffect(() => {
    engine.setApplyDraftGlobally(draft.livePreview);
  }, [engine, draft.livePreview]);

  /**
   * The mode toggle switches the document, not just the preview.
   *
   * Tailwind's dark: variant matches any ancestor, so a "light" island inside
   * html.dark still renders dark - a preview claiming to show the light values
   * while painting the dark ones is worse than no preview. Moving the document
   * makes it true everywhere at once, and has the side benefit that the nav and
   * top bar around the editor show the mode being edited too.
   *
   * Only the class moves. CollapsibleDashboard owns the user's real preference
   * in localStorage['theme'] and is deliberately left alone, so this reverts on
   * the way out and their setting survives the visit.
   */
  useEffect(() => {
    const root = document.documentElement;
    const was = root.classList.contains('dark');
    const want = mode === 'dark';
    if (was === want) return undefined;
    root.classList.toggle('dark', want);
    return () => { root.classList.toggle('dark', was); };
  }, [mode]);

  /** Jump from the preview inspector to the control that changes a token. */
  const jumpToControl = useCallback((target: Tab, token: string) => {
    setTab(target);
    setHighlighted(token);
    // Let the tab render before scrolling to the control inside it.
    requestAnimationFrame(() => {
      document.getElementById(`control-${token}`)?.scrollIntoView({ block: 'center' });
    });
  }, []);

  // The mark is for finding the row, not for keeping. Left on, a second jump
  // would leave two rows claiming to be the one you asked for.
  useEffect(() => {
    if (!highlighted) return undefined;
    const timer = setTimeout(() => setHighlighted(null), 2500);
    return () => clearTimeout(timer);
  }, [highlighted]);

  // With site-wide preview off the engine paints the SAVED theme, so the panel
  // has to render the draft itself. Scoped to .theme-preview, which
  // token-components.css lists alongside :root so the component aliases are
  // re-substituted there rather than staying frozen at the root's values.
  useEffect(() => {
    const id = 'theme-engine-preview';
    const existing = document.getElementById(id);
    // The dark selector follows the MODE, not a nested wrapper. The preview used
    // to be a `.dark` island, so `.theme-preview.dark` was the right target; now
    // the document itself carries the mode and the stage never gets that class.
    // Left as it was, editing dark with site-wide preview off would paint the
    // draft's LIGHT overrides on top of the base DARK tokens - a preview showing
    // a combination that exists nowhere.
    //
    // Light stays as the first rule in both cases because compileTheme puts the
    // mode-independent typography and layout there; the dark rule is emitted
    // second, so at equal specificity it wins the colours it declares.
    const css = draft.livePreview ? '' : compileTheme(engine.resolved, {
      lightSelector: '.theme-preview',
      darkSelector: mode === 'dark'
        ? '.theme-preview'
        : '.theme-preview .dark, .theme-preview.dark',
    });

    if (!css) {
      existing?.remove();
      return;
    }
    const el = (existing as HTMLStyleElement) ?? document.createElement('style');
    el.id = id;
    if (el.textContent !== css) el.textContent = css;
    if (!existing) document.head.appendChild(el);
  }, [draft.livePreview, engine.resolved, mode]);

  useEffect(() => () => document.getElementById('theme-engine-preview')?.remove(), []);

  return (
    <div
      className="p-6"
      style={{ '--space-unit': '0.25rem' } as React.CSSProperties}
    >
      <header className="mb-4">
        <h1 className="text-xl font-semibold text-heading">Customize</h1>
        <p className="mt-1 text-sm text-muted">
          Change how FastCMS looks. Edits apply to your whole session immediately, so you can
          navigate around and see them on real screens before saving.
        </p>
      </header>

      {/* Scope */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex overflow-hidden rounded border border-gray-300 dark:border-gray-600">
          {(['user', 'org'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => draft.setScope(s)}
              className={`px-3 py-1.5 text-sm ${scope === s
                ? 'bg-blue-600 text-white'
                : 'bg-panel text-gray-700 hover:bg-highlight dark:text-gray-200'}`}
            >
              {s === 'user' ? 'My theme' : 'Organization default'}
            </button>
          ))}
        </div>

        <div className="inline-flex overflow-hidden rounded border border-gray-300 dark:border-gray-600">
          {(['light', 'dark'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => draft.setMode(m)}
              className={`px-3 py-1.5 text-sm capitalize ${mode === m
                ? 'bg-gray-700 text-white'
                : 'bg-panel text-gray-700 hover:bg-highlight dark:text-gray-200'}`}
            >
              {m}
            </button>
          ))}
        </div>
        <span className="text-xs text-muted">
          Editing the {mode} values
        </span>

        <label className="ml-auto flex items-center gap-2 text-sm text-heading">
          <input
            type="checkbox"
            checked={draft.livePreview}
            onChange={(e) => draft.setLivePreview(e.target.checked)}
          />
          <span>Preview on the whole site</span>
        </label>
      </div>

      {scope === 'org' && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded border border-gray-300
                        bg-gray-50 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
        >
          <label className="flex items-center gap-2 text-heading">
            <input
              type="checkbox"
              checked={draft.orgEnabled}
              onChange={(e) => draft.setOrgEnabled(e.target.checked)}
            />
            <span className="font-medium">Theming on for everyone</span>
          </label>
          <span className="text-xs text-muted">
            {draft.orgEnabled
              ? 'Publishing applies this theme to every user.'
              : 'Publishing saves the theme but leaves the app looking stock for everyone.'}
            {' '}
            {!engine.orgTheme?.enabled && 'Theming is currently off.'}
          </span>
        </div>
      )}

      {legacy && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded border border-blue-300
                        bg-blue-50 px-3 py-2 text-sm dark:border-blue-700 dark:bg-blue-900/30"
        >
          <span className="text-blue-900 dark:text-blue-100">
            You had custom theme colors set under the old Settings panel. Bring them across?
          </span>
          <button
            type="button"
            onClick={() => answerLegacy(true)}
            className="rounded bg-accent px-2 py-1 text-xs text-accent-fg hover:bg-accent-hover"
          >
            Import them
          </button>
          <button
            type="button"
            onClick={() => answerLegacy(false)}
            className="px-1 text-xs text-blue-900 underline dark:text-blue-100"
          >
            No thanks
          </button>
        </div>
      )}

      <PresetBar
        scope={scope}
        working={working}
        onApply={(doc) => draft.patch(applyPreset(working, doc))}
      />

      <div className="grid gap-4 xl:grid-cols-[380px_minmax(0,1fr)]">
        {/* Editor */}
        <div className="rounded border border-gray-200 bg-panel p-3 dark:border-gray-700">
          <div className="mb-2 flex flex-wrap gap-1">
            {(['colors', 'type', 'layout', 'components'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`rounded px-2 py-1 text-xs capitalize ${tab === t
                  ? 'bg-gray-200 font-semibold text-heading dark:bg-gray-700'
                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
              >
                {t === 'type' ? 'Typography' : t}
              </button>
            ))}
          </div>

          <div className="max-h-[60vh] overflow-y-auto pr-1">
            {tab === 'colors' && COLOR_GROUPS.map((groupDef) => (
              <Section key={groupDef.title} title={groupDef.title}>
                {groupDef.tokens.map(({ token, label, hint }) => (
                  <ColorField
                    key={token}
                    label={label}
                    hint={hint}
                    anchor={token}
                    flash={highlighted === token}
                    value={effective('tokens', token)}
                    overridden={isOverridden('tokens', token)}
                    onChange={(v) => draft.setToken('tokens', token, v)}
                    onReset={() => draft.setToken('tokens', token, '')}
                  />
                ))}
              </Section>
            ))}

            {tab === 'type' && (
              <Section title="Typography">
                <SliderField
                  label="Base text size"
                  hint="Scales the whole interface, text and spacing together"
                  value={working.typography?.fontSizePx ?? 17}
                  min={MIN_FONT_PX}
                  max={MAX_FONT_PX}
                  suffix="px"
                  overridden={working.typography?.fontSizePx !== undefined}
                  onChange={(v) => draft.patch({
                    typography: { ...working.typography, fontSizePx: v },
                  })}
                  onReset={() => draft.patch({
                    typography: { ...working.typography, fontSizePx: undefined },
                  })}
                />
                {(['normal', 'medium', 'semibold', 'bold'] as const).map((key) => (
                  <SliderField
                    key={key}
                    label={`${key[0].toUpperCase()}${key.slice(1)} weight`}
                    value={working.typography?.weights?.[key]
                      ?? { normal: 400, medium: 500, semibold: 600, bold: 700 }[key]}
                    min={100}
                    max={900}
                    step={100}
                    overridden={working.typography?.weights?.[key] !== undefined}
                    onChange={(v) => draft.patch({
                      typography: {
                        ...working.typography,
                        weights: { ...working.typography?.weights, [key]: v },
                      },
                    })}
                    onReset={() => {
                      const weights = { ...working.typography?.weights };
                      delete weights[key];
                      draft.patch({ typography: { ...working.typography, weights } });
                    }}
                  />
                ))}
                <p className="mt-2 text-xs text-muted">
                  Weights outside 400-700 may render as a synthesised faux-bold on machines that
                  do not have that cut of the typeface.
                </p>
              </Section>
            )}

            {tab === 'layout' && (
              <Section title="Density & shape">
                <SliderField
                  label="Spacing"
                  hint="Tightens or loosens padding and gaps without changing text size"
                  value={Math.round((working.layout?.spaceUnitRem ?? 0.25) * 100)}
                  min={Math.round(MIN_SPACE_UNIT_REM * 100)}
                  max={Math.round(MAX_SPACE_UNIT_REM * 100)}
                  overridden={working.layout?.spaceUnitRem !== undefined}
                  onChange={(v) => draft.patch({
                    layout: { ...working.layout, spaceUnitRem: v / 100 },
                  })}
                  onReset={() => draft.patch({
                    layout: { ...working.layout, spaceUnitRem: undefined },
                  })}
                />
                <p className="mt-2 text-xs text-muted">
                  Spacing changes the room between things. Text, icons and controls
                  keep their size at every setting.
                </p>
              </Section>
            )}

            {tab === 'components' && (
              <>
                {/* These scope the token GALLERY, so choosing one switches the
                    preview to it - a picker whose click changes nothing visible
                    reads as broken. */}
                <Section title="Component">
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => { setGallerySection(null); setShowGallery(true); }}
                      className={`rounded px-2 py-1 text-xs ${gallerySection === null
                        ? 'bg-gray-200 font-semibold dark:bg-gray-700 dark:text-white'
                        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                    >
                      All
                    </button>
                    {GALLERY_SECTIONS.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => { setGallerySection(s.id); setShowGallery(true); }}
                        className={`rounded px-2 py-1 text-xs ${gallerySection === s.id
                          ? 'bg-gray-200 font-semibold dark:bg-gray-700 dark:text-white'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </Section>

                {GALLERY_SECTIONS
                  .filter((s) => gallerySection === null || s.id === gallerySection)
                  .map((s) => (
                    <Section key={s.id} title={s.wired ? s.label : `${s.label} (preview only)`}>
                      {!s.wired && (
                        <p className="mb-1 text-xs text-amber-700 dark:text-amber-300">
                          The real component has not been wired to these tokens yet, so
                          changes here show in the preview but not in the app.
                        </p>
                      )}
                      {s.tokens.filter((t) => !/-(radius|pad|font-size|pad-x|pad-y)$/.test(t))
                        .map((token) => (
                          <ColorField
                            key={token}
                            anchor={token}
                            flash={highlighted === token}
                            label={token.replace(/^cmp-/, '').replace(/-/g, ' ')}
                            value={effective('components', token)}
                            overridden={isOverridden('components', token)}
                            onChange={(v) => draft.setToken('components', token, v)}
                            onReset={() => draft.setToken('components', token, '')}
                          />
                        ))}
                    </Section>
                  ))}
              </>
            )}
          </div>

          {/* Contrast */}
          {failing.length > 0 && (
            <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-2
                            dark:border-amber-700 dark:bg-amber-900/30"
            >
              <div className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                {failing.length} contrast {failing.length === 1 ? 'issue' : 'issues'} in {mode} mode
              </div>
              <ul className="mt-1 space-y-0.5">
                {failing.map((c) => (
                  <li key={c.label} className="text-xs text-amber-900 dark:text-amber-200">
                    {c.label}: {c.ratio}:1
                    {' '}
                    <span className="opacity-75">(WCAG AA needs 4.5:1)</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="rounded border border-gray-200 bg-panel p-3 dark:border-gray-700">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted">
              Preview — {mode} mode
            </div>
            <button
              type="button"
              onClick={() => setShowGallery((v) => !v)}
              className="text-xs text-gray-600 underline decoration-dotted underline-offset-2
                         dark:text-gray-300"
            >
              {showGallery ? 'Show page samples' : 'Show the token gallery'}
            </button>
          </div>

          {/* The draft's density is re-applied here: the page root pinned it back
              to stock so the editor's own controls would hold still while you
              drag the thing that moves them. The preview has to show the real
              value or it stops being one. */}
          {showGallery ? (
            <div
              className="theme-preview"
              style={{ '--space-unit': previewSpaceUnit } as React.CSSProperties}
            >
              <Gallery selected={gallerySection} mode={mode} />
            </div>
          ) : (
            <ScenePreview
              mode={mode}
              spaceUnit={previewSpaceUnit}
              onJumpTo={jumpToControl}
            />
          )}
        </div>
      </div>

      {/* Draft bar */}
      {isDirty && (
        <div
          className="sticky bottom-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-3
                     rounded border border-gray-300 bg-white p-3 shadow-lg
                     dark:border-gray-600 dark:bg-gray-800"
          style={{ fontSize: CHROME_FONT_SIZE }}
        >
          <div className="text-gray-700 dark:text-gray-200">
            Unsaved changes to{' '}
            <strong>{publishing ? 'the organization default' : 'your theme'}</strong>.
            {publishing && ' Publishing changes how FastCMS looks for everyone.'}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={draft.revert}
              disabled={busy}
              className="rounded border border-gray-300 px-3 py-1.5 text-gray-700
                         hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600
                         dark:text-gray-200 dark:hover:bg-gray-700"
            >
              Revert
            </button>
            {publishing ? (
              <PermissionsContainer componentId="edit-org-theme" componentName="Organization Theme" defaultDisabled>
                <PublishButton busy={busy} onClick={onSave} />
              </PermissionsContainer>
            ) : (
              <button
                type="button"
                onClick={onSave}
                disabled={busy}
                className="rounded bg-blue-600 px-3 py-1.5 font-medium text-white
                           hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? 'Saving...' : 'Save my theme'}
              </button>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800
                        dark:border-red-700 dark:bg-red-900/30 dark:text-red-200"
        >
          {error}
        </div>
      )}
    </div>
  );
};

/**
 * The preset library.
 *
 * Applying REPLACES the draft's tokens rather than merging (see applyPreset),
 * so picking a second preset never leaves fragments of the first behind.
 * Saving is scope-aware: from "My theme" it writes a personal preset, from
 * "Organization default" it writes one the whole company can pick.
 */
const PresetBar: React.FC<{
  scope: 'user' | 'org';
  working: ThemeDoc;
  onApply: (doc: ThemeDoc) => void;
}> = ({ scope, working, onApply }) => {
  const { entries, error, save, remove } = useCustomizePresets(scope);
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  const onSave = async () => {
    if (await save(name, working)) {
      setNaming(false);
      setName('');
    }
  };

  const badge = (kind: PresetEntry['kind']) => {
    if (kind === 'built-in') return null;
    return (
      <span
        className="ml-1 rounded px-1 text-muted"
        style={{ fontSize: '10px' }}
      >
        {kind === 'org' ? 'org' : 'mine'}
      </span>
    );
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded border border-gray-300
                    bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-800"
    >
      <span className="text-xs font-semibold uppercase tracking-wide text-muted">Presets</span>

      {entries.map((entry) => (
        <span key={`${entry.kind}-${entry.slug}`} className="inline-flex items-center">
          <button
            type="button"
            onClick={() => onApply(entry.doc)}
            title={entry.kind === 'built-in' ? undefined : `Saved ${entry.kind} preset`}
            className="rounded border border-gray-300 bg-panel px-2 py-1 text-xs text-body
                       hover:bg-highlight dark:border-gray-600"
          >
            {entry.name}
            {badge(entry.kind)}
          </button>
          {entry.deletable && (
            confirmingDelete === `${entry.kind}-${entry.slug}` ? (
              <span className="ml-1 inline-flex items-center gap-1" style={{ fontSize: '11px' }}>
                <button
                  type="button"
                  onClick={async () => { await remove(entry); setConfirmingDelete(null); }}
                  className="rounded bg-red-600 px-1.5 py-0.5 text-white hover:bg-red-700"
                >
                  delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(null)}
                  className="text-muted hover:underline"
                >
                  cancel
                </button>
              </span>
            ) : (
              <button
                type="button"
                aria-label={`Delete preset ${entry.name}`}
                onClick={() => setConfirmingDelete(`${entry.kind}-${entry.slug}`)}
                className="ml-0.5 px-1 text-muted hover:text-red-600"
                style={{ fontSize: '11px' }}
              >
                x
              </button>
            )
          )}
        </span>
      ))}

      {naming ? (
        <span className="inline-flex items-center gap-1">
          <input
            autoFocus
            aria-label="Preset name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') setNaming(false); }}
            placeholder="Preset name"
            className="w-36 rounded border border-gray-300 bg-panel px-2 py-1 text-xs text-body
                       dark:border-gray-600"
          />
          <button
            type="button"
            onClick={onSave}
            aria-label="Save preset"
            className="rounded bg-accent px-2 py-1 text-xs text-accent-fg hover:bg-accent-hover"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setNaming(false)}
            className="px-1 text-xs text-muted hover:underline"
          >
            cancel
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setNaming(true)}
          className="rounded border border-dashed border-gray-400 px-2 py-1 text-xs text-muted
                     hover:text-body dark:border-gray-500"
        >
          + Save current as{scope === 'org' ? ' org preset' : ' preset'}
        </button>
      )}

      {error && <span className="text-xs text-red-600 dark:text-red-400">{error}</span>}
    </div>
  );
};

/** Publish needs a confirmation: it is the one action other people can see. */
const PublishButton: React.FC<{ busy: boolean; onClick: () => void }> = ({ busy, onClick }) => {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={busy}
        className="rounded bg-blue-600 px-3 py-1.5 font-medium text-white
                   hover:bg-blue-700 disabled:opacity-50"
      >
        Publish to everyone
      </button>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <span className="text-gray-700 dark:text-gray-200">Publish for all users?</span>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        className="rounded border border-gray-300 px-2 py-1 dark:border-gray-600 dark:text-gray-200"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={() => { setConfirming(false); onClick(); }}
        disabled={busy}
        className="rounded bg-red-600 px-3 py-1.5 font-medium text-white hover:bg-red-700 disabled:opacity-50"
      >
        {busy ? 'Publishing...' : 'Yes, publish'}
      </button>
    </span>
  );
};

export default Customize;
